import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  User,
} from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { arrayRemove } from '@firebase/firestore';
import { auth, db } from '../firebase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signOut = async () => {
    // Before dropping the Firebase Auth session, clean up device-bound state so
    // the next user on this phone doesn't inherit the previous user's FCM
    // subscription, offline queue, or cached temp photos.
    const currentUser = auth.currentUser;
    const fcmToken = localStorage.getItem('fcm_token');

    if (currentUser && fcmToken) {
      try {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          fcmTokens: arrayRemove(fcmToken)
        });
      } catch (err) {
        // Non-fatal — token will still work until it naturally expires.
        console.warn('Failed to remove FCM token on signOut:', err);
      }
    }

    try {
      localStorage.removeItem('fcm_token');
      localStorage.removeItem('offlinePhotos');
      localStorage.removeItem('deletedOfflinePhotos');
      localStorage.removeItem('temporaryPhotos');
    } catch (err) {
      console.warn('Failed to clear local caches on signOut:', err);
    }

    await firebaseSignOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
